import { RadioButton } from '@altify/components';
import { AiInsightsIcon } from 'assets/icons/AiInsightsIcon';
import clsx from 'clsx';
import { useAiInsights } from 'components/AiInsights/aiInsights-context';
import { AiInsightsModalCallable } from 'components/AiInsights/AiInsightsBottomSheet';
import { parseAiInsightsQuestions } from 'components/AiInsights/utils/questionParser';
import { useProductsGraphs } from 'hooks/products/useGraphs';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import useBreakpoint from 'hooks/useBreakpoint';
import { useState } from 'react';
import Slider from 'react-slick';
import { Settings } from 'react-slick';

export interface AiInsightsTabItem<T extends string> {
  label: string;
  className?: string;
  icon?: any;
  order: number;
}

interface ArrowProps {
  onClick?: () => void;
  className?: string;
}

const ARROW_BASE_CLASSES =
  'group absolute top-1/2 -translate-y-1/2 z-10 cursor-pointer w-10 h-10 flex items-center justify-center bg-black rounded-full border border-white hover:bg-[#B393FF] hover:border-[#B393FF]';

const NextArrow = ({ onClick, className }: ArrowProps) => {
  const isDisabled = className?.includes('slick-disabled');

  if (isDisabled) return null;

  return (
    <div className={clsx(ARROW_BASE_CLASSES, 'right-0')} onClick={onClick}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        className="stroke-white group-hover:stroke-neutral-600">
        <path
          d="M4 2L8 6L4 10"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const PrevArrow = ({ onClick, className }: ArrowProps) => {
  const isDisabled = className?.includes('slick-disabled');

  if (isDisabled) return null;

  return (
    <div className={clsx(ARROW_BASE_CLASSES, 'left-0')} onClick={onClick}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        className="stroke-white group-hover:stroke-neutral-600">
        <path
          d="M8 2L4 6L8 10"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const getSliderSettings = (numOfSlides: number): Settings => ({
  className: 'slider variable-width',
  dots: false,
  infinite: false,
  centerMode: false,
  variableWidth: true,
  arrows: true,
  swipeToSlide: true,
  draggable: true,
  slidesToScroll: numOfSlides - 1,
  slidesToShow: numOfSlides - 1,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  edgeFriction: 100,
});

const getButtonClasses = (isActive: boolean, customClassName?: string) =>
  clsx(
    'group px-6 mx-1 h-10 bg-prim-black border border-[#B393FF] hover:!bg-[#B393FF] active:!bg-[#B393FF]',
    isActive && 'bg-[#B393FF]',
    customClassName,
  );


const getTextClasses = (isActive: boolean) =>
  isActive ? 'text-neutral-600' : 'text-white group-hover:text-white';

const getIconClasses = (isActive: boolean) =>
  isActive
    ? `stroke-neutral-600`
    : `stroke-[#B393FF] group-hover:stroke-white`;

export function AiInsightsTabs({
  pageName,
  productType,
  productCode,
  onClickItem,
  className,
}: {
  pageName: 'Portfolio' | 'Assets' | 'Explore';
  productType?: string;
  productCode?: string;
  onClickItem: (question: string) => void;
  className?: string;
}) {
  const { isSmaller } = useBreakpoint();
  const isDeviceMobile = isSmaller('lg');
  const { question, isOpen: isDrawerOpen } = useAiInsights();
  const { functionalCurrency } = useFunctionalCurrency();
  const productGraph = useProductsGraphs('24h', {
    displayCurrency: functionalCurrency?.code || 'ZAR',
  });
  const { products } = useProductsWithHoldingsAndRates();
  const aiInsightsQuestions = parseAiInsightsQuestions(
    pageName,
    products || [],
    productGraph,
    productCode,
    productType,
  );

  const settings = getSliderSettings(
    isDrawerOpen
      ? aiInsightsQuestions?.length - 1
      : aiInsightsQuestions?.length,
  );
  return (
    <>
      {isDeviceMobile ? (
        <div className="flex flex-row overflow-x-auto no-scrollbar">
          {aiInsightsQuestions
            ?.sort((a, b) => a.value - b.value)
            .map((item, i) => {
              const isActive = question === item.label;
              return (
                <RadioButton
                  selected={isActive}
                  className={getButtonClasses(false, className)}
                  textClassName={getTextClasses(false)}
                  iconClassname={getIconClasses(false)}
                  title={item.label}
                  onPress={() => {
                    AiInsightsModalCallable.call({
                      pageName: pageName,
                      question: item.label,
                    });
                  }}
                  size="small"
                  weight="normal"
                  iconComponent={AiInsightsIcon}
                />
              );
            })}
        </div>
      ) : (
        <div className="slider-container">
          <Slider {...settings}>
            {aiInsightsQuestions
              ?.sort((a, b) => a.value - b.value)
              .map((item, i) => {
                const isActive = question === item.label;
                return (
                  <div key={i}>
                    <RadioButton
                      selected={isActive}
                      className={getButtonClasses(isActive, className)}
                      textClassName={getTextClasses(isActive)}
                      iconClassname={getIconClasses(isActive)}
                      title={item.label}
                      onPress={() => {
                        onClickItem(item.label);
                      }}
                      size="small"
                      weight="normal"
                      iconComponent={AiInsightsIcon}
                    />
                  </div>
                );
              })}
          </Slider>
        </div>
      )}
    </>
  );
}

export default AiInsightsTabs;
